import mixpanel from "mixpanel-browser";

import constants from "../Constant";
import appInfo from "./app-info";
import { MIXPANEL_API_HOST } from "./mixpanel-config";

function logDebug(message, ...optionalParams) {
  if (constants.ENV_NAME !== "prod") {
    console.debug(message, ...optionalParams);
  }
}

// Storage key for device ID
const DEVICE_ID_STORAGE_KEY = "mixpanel_device_id";

// Check if we already have a stored device ID
const existingDeviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);

// Register app info as Mixpanel super properties. Called at init time (via
// the `loaded` callback so it runs before the initial $mp_web_page_view) and
// again after mixpanel.reset() so super props survive logout+login in the
// same tab. Mirrors the mobile app's setAppInfo() behaviour.
const registerAppInfo = () => {
  mixpanel.register({
    "App Name": appInfo.name,
    "App Version": appInfo.version,
    "App Platform": appInfo.platform,
    "App Timezone": appInfo.timeZone,
  });
};

// Initialize mixpanel with the existing device ID if available
mixpanel.init(constants.MIXPANEL_TOKEN, {
  api_host: MIXPANEL_API_HOST,
  debug: constants.ENV_NAME !== "prod" ? true : false,
  autocapture: true,
  track_pageview: true,
  persistence: "localStorage",
  loaded: () => registerAppInfo(),
  ...(existingDeviceId && { device_id: existingDeviceId }),
});

registerAppInfo();

// Store device ID if it doesn't exist yet
if (!existingDeviceId) {
  const generatedDeviceId = mixpanel.get_property("$device_id");
  if (generatedDeviceId) {
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, generatedDeviceId);
    logDebug("Mixpanel device ID stored in localStorage", generatedDeviceId);
  }
}

/**
 * Get UTM parameters from Mixpanel's automatically tracked properties
 */
export function getUtmParameters() {
  // Standard and extended UTM parameters to check
  const utmNames = [
    // Standard UTM parameters
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    // Extended UTM parameters
    "utm_id",
    "utm_source_platform",
    "utm_campaign_id",
    "utm_creative_format",
    "utm_marketing_tactic",
  ];

  const utmParams = {};
  let hasParams = false;

  // Check each UTM parameter in Mixpanel
  utmNames.forEach((paramName) => {
    const value = mixpanel.get_property(paramName);
    if (value) {
      utmParams[paramName] = value;
      hasParams = true;
    }
  });

  logDebug(
    hasParams ? "Found UTM parameters" : "No UTM parameters found",
    utmParams
  );

  return hasParams ? utmParams : null;
}

export const setUserId = (userId) => {
  logDebug("Mixpanel setUserId", userId);
  if (!userId) {
    // See: https://github.com/mixpanel/mixpanel-js/issues/67
    mixpanel.reset();
    registerAppInfo();
  } else {
    mixpanel.identify(userId);
  }
};

export const getDeviceId = () => {
  // Check localStorage first for the most up-to-date value
  const storedDeviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (storedDeviceId) {
    logDebug("Mixpanel getDeviceId from localStorage", storedDeviceId);
    return storedDeviceId;
  }

  // Fall back to mixpanel's stored value
  const deviceId = mixpanel.get_property("$device_id");
  logDebug("Mixpanel getDeviceId", deviceId);

  // Store it if we have it now
  if (deviceId) {
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  }

  return deviceId;
};

export const trackEvent = (event, properties, callback) => {
  // Prefix all frontend events with "[Frontend]"
  if (!event.startsWith("[Frontend]")) {
    event = `[Frontend] ${event}`;
  }
  logDebug("Mixpanel trackEvent", event, properties);
  mixpanel.track(event, properties, callback);
};
